import { useMemo } from 'react'
import { Card } from '../../SharedComponents/Card'
import { DataTable } from './DataTable'
import {
  formatCurrency,
  formatNegative,
  parseAmount,
} from '../../../lib/utils/format'
import type {
  ClawbackBreakdownCardProps,
  ClawbackBreakdownRow,
  Column,
} from '../types'
import styles from '../index.module.css'

const columns: Column<ClawbackBreakdownRow>[] = [
  { title: 'Month', key: 'month', accessor: 'month' },
  {
    title: 'Paid Units',
    key: 'paid_units',
    accessor: 'paid_units',
  },
  {
    title: 'Total Debt Paid',
    key: 'total_debt_paid',
    render: (r) => formatCurrency(r.total_debt_paid),
  },
  {
    title: 'BPS',
    key: 'bps',
    accessor: 'bps',
  },
  {
    title: 'Unit Bonus',
    key: 'unit_bonus',
    render: (r) => formatCurrency(r.unit_bonus),
  },
  {
    title: 'Clawback (#)',
    key: 'clawbacks',
    accessor: 'clawbacks',
  },
  {
    title: 'Clawback ($)',
    key: 'clawback_value',
    render: (r) => formatNegative(r.clawback_value),
  },
  {
    title: 'Total Clawback (#)',
    key: 'total_clawbacks',
    accessor: 'total_clawbacks',
  },
  {
    title: 'Total Clawback ($)',
    key: 'total_clawback_value',
    render: (r) => formatNegative(r.total_clawback_value),
  },
]
// TODO: move to BE
const MONTH_ORDER = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

// TODO: move to BE
const sumNumeric = (
  rows: ClawbackBreakdownRow[],
  key: keyof ClawbackBreakdownRow,
): number => rows.reduce((acc, r) => acc + (parseAmount(r[key]) ?? 0), 0)

const averageBps = (rows: ClawbackBreakdownRow[]): string => {
  if (rows.length === 0) return '—'
  return (sumNumeric(rows, 'bps') / rows.length).toFixed(5)
}

export default function ClawbackBreakdownCard({
  rows,
  loading,
}: ClawbackBreakdownCardProps) {
  // temporary hack. BE will return the rows in the correct order later
  const sortedRows = useMemo(
    () =>
      rows
        .slice()
        .sort(
          (a, b) => MONTH_ORDER.indexOf(a.month) - MONTH_ORDER.indexOf(b.month),
        ),
    [rows],
  )
  // TODO: move to BE when they return the aggregated totals
  const summaryRow = useMemo(
    () => [
      'Total',
      sumNumeric(sortedRows, 'paid_units'),
      formatCurrency(sumNumeric(sortedRows, 'total_debt_paid')),
      averageBps(sortedRows),
      formatCurrency(sumNumeric(sortedRows, 'unit_bonus')),
      sumNumeric(sortedRows, 'clawbacks'),
      formatNegative(sumNumeric(sortedRows, 'clawback_value')),
      sumNumeric(sortedRows, 'total_clawbacks'),
      formatNegative(sumNumeric(sortedRows, 'total_clawback_value')),
    ],
    [sortedRows],
  )

  return (
    <Card>
      <div className={styles.cardHeader}>Clawback Breakdown</div>
      <DataTable
        columns={columns}
        data={sortedRows}
        rowKey={(_r, i) => String(i)}
        loading={loading}
        summaryRow={summaryRow}
      />
    </Card>
  )
}
